'use client';

import { AnimatePresence, motion } from 'framer-motion';
import Link from 'next/link';
import { useState, useEffect } from 'react';
import { IoIosMenu, IoIosClose } from 'react-icons/io';

import { LocaleSwitcher } from '../locale-switcher';
import { Button } from '@/components/elements/button';
import { Logo } from '@/components/logo';
import { cn } from '@/lib/utils';

type Props = {
  leftNavbarItems: {
    URL: string;
    text: string;
    target?: string;
    children?: { URL: string; text: string; target?: string }[];
  }[];
  rightNavbarItems: {
    URL: string;
    text: string;
    target?: string;
  }[];
  logo: any;
  locale: string;
};

export const MobileNavbar = ({
  leftNavbarItems,
  rightNavbarItems,
  logo,
  locale,
}: Props) => {
  const [open, setOpen] = useState(false);

  // Prevent scrolling when the mobile menu is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  return (
    <div className="flex justify-between items-center w-full px-4 py-3 transition-colors duration-200">
      <Logo image={logo?.image} />

      <button
        onClick={() => setOpen(true)}
        className="p-2 -mr-2 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50"
        aria-label="Open menu"
      >
        <IoIosMenu className="text-neutral-900 dark:text-white h-7 w-7" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="fixed inset-0 z-[100] flex flex-col bg-white/95 dark:bg-neutral-950/95 backdrop-blur-xl border-b border-neutral-200 dark:border-neutral-800 overflow-hidden h-[100dvh]"
          >
            <div className="flex items-center justify-between w-full px-4 py-3 border-b border-neutral-100 dark:border-neutral-800/50">
              <Logo locale={locale} image={logo?.image} />
              <div className="flex items-center space-x-4">
                <LocaleSwitcher currentLocale={locale} />
                <button
                  onClick={() => setOpen(false)}
                  className="p-2 -mr-2 rounded-full bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50"
                  aria-label="Close menu"
                >
                  <IoIosClose className="h-7 w-7 text-neutral-900 dark:text-white" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="flex flex-col px-6 py-8 gap-8">
                {leftNavbarItems?.map((navItem, idx) => (
                  <motion.div
                    key={`nav-${idx}`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + idx * 0.05 }}
                    className="flex flex-col gap-4"
                  >
                    {navItem.children && navItem.children.length > 0 ? (
                      <>
                        <div className="text-xs font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest">
                          {navItem.text}
                        </div>
                        <div className="flex flex-col gap-4 pl-3 border-l border-neutral-200 dark:border-neutral-800">
                          {navItem.children.map((childNavItem, childIdx) => (
                            <Link
                              key={`child-${childIdx}`}
                              href={`/${locale}${childNavItem.URL}`}
                              onClick={() => setOpen(false)}
                              className="text-2xl font-medium text-neutral-800 dark:text-neutral-200 hover:text-primary dark:hover:text-primary transition-colors"
                            >
                              {childNavItem.text}
                            </Link>
                          ))}
                        </div>
                      </>
                    ) : (
                      <Link
                        href={`/${locale}${navItem.URL}`}
                        onClick={() => setOpen(false)}
                        className="text-3xl font-bold text-neutral-900 dark:text-white hover:text-primary dark:hover:text-primary transition-colors"
                      >
                        {navItem.text}
                      </Link>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-auto p-6 flex flex-col gap-4 bg-neutral-50/50 dark:bg-neutral-900/50 border-t border-neutral-100 dark:border-neutral-800 pb-12"
            >
              {rightNavbarItems?.map((item) => (
                <Button
                  key={item.text}
                  variant="primary"
                  as={Link}
                  href={`/${locale}${item.URL}`}
                  className="w-full justify-center text-lg py-6 rounded-2xl shadow-lg shadow-primary/20"
                  onClick={() => setOpen(false)}
                >
                  {item.text}
                </Button>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
